import { useNavigate } from 'react-router-dom';

import { formatMentionDate, ratingStars, sourceMeta } from '../utils/sentiment.js';
import MentionStatusBadge from './MentionStatusBadge.jsx';
import SentimentBadge from './SentimentBadge.jsx';
import SeverityBadge from './SeverityBadge.jsx';
import SourceIcon from './SourceIcon.jsx';
import UrgentBadge from './UrgentBadge.jsx';

const COLUMNS = [
  { key: 'actions', label: '' },
  { key: 'published', label: 'Published', sortKey: 'published_at' },
  { key: 'source', label: 'Source' },
  { key: 'location', label: 'Location' },
  { key: 'rating', label: 'Rating', sortKey: 'rating' },
  { key: 'content', label: 'Content' },
  { key: 'sentiment', label: 'Sentiment' },
  { key: 'urgent', label: 'Urgent', sortKey: 'is_urgent' },
  { key: 'severity', label: 'Severity', sortKey: 'severity' },
  { key: 'migration', label: 'Evolution' },
  { key: 'cc', label: 'CC', sortKey: 'submitted_to_command_center' },
  { key: 'status', label: 'Status' },
];

function truncate(s, n) {
  if (!s) return '';
  return s.length > n ? `${s.slice(0, n - 1)}…` : s;
}

function SkeletonRow() {
  return (
    <tr>
      {COLUMNS.map((c, i) => (
        <td key={c.key}>
          <div className="skeleton-cell" style={{ width: i === 5 ? '80%' : '55%' }} />
        </td>
      ))}
    </tr>
  );
}

export default function MentionsTable({
  mentions,
  loading,
  onStatusChange,
  sortBy,
  sortOrder,
  onSortChange,
  page,
  pageSize,
  total,
  pages,
  onPageChange,
  onClearFilters,
}) {
  const navigate = useNavigate();
  const showSkeleton = loading && mentions.length === 0;
  const showEmpty = !loading && mentions.length === 0;
  const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);

  function toggleSort(sortKey) {
    if (!sortKey) return;
    if (sortBy === sortKey) {
      onSortChange(sortKey, sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      onSortChange(sortKey, 'desc');
    }
  }

  function sortIndicator(sortKey) {
    if (!sortKey || sortBy !== sortKey) return null;
    return sortOrder === 'asc' ? ' ▲' : ' ▼';
  }

  return (
    <div className="table-wrap">
      <div className="table-scroll">
        <table className="submissions">
          <thead>
            <tr>
              {COLUMNS.map((c) => (
                <th
                  key={c.key}
                  className={c.sortKey ? 'sortable' : undefined}
                  onClick={c.sortKey ? () => toggleSort(c.sortKey) : undefined}
                >
                  {c.label}
                  {sortIndicator(c.sortKey)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {showSkeleton &&
              Array.from({ length: 6 }).map((_, i) => <SkeletonRow key={i} />)}
            {!showSkeleton &&
              mentions.map((m) => {
                const meta = sourceMeta(m.source_type);
                return (
                  <tr key={m.id}>
                    <td>
                      <button
                        className="btn btn--secondary"
                        onClick={() => navigate(`/mentions/${m.id}`)}
                      >
                        View
                      </button>
                    </td>
                    <td className="table__received">{formatMentionDate(m.published_at)}</td>
                    <td>
                      <span title={meta.label} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <SourceIcon type={m.source_type} /> {meta.label}
                      </span>
                    </td>
                    <td>{m.location_name || '—'}</td>
                    <td title={m.rating ? `${m.rating}/5` : ''}>{ratingStars(m.rating)}</td>
                    <td>
                      <div className="table__intent" title={m.content || ''}>
                        {truncate(m.content, 80) || '—'}
                      </div>
                    </td>
                    <td><SentimentBadge value={m.sentiment} /></td>
                    <td>
                      {m.is_urgent ? (
                        <UrgentBadge />
                      ) : (
                        <span style={{ color: 'var(--color-text-muted)' }}>—</span>
                      )}
                    </td>
                    <td><SeverityBadge value={m.severity} /></td>
                    <td>
                      {m.is_migration_related ? (
                        <span title="Potentially Evolution-related">🔴</span>
                      ) : (
                        <span style={{ color: 'var(--color-text-muted)' }}>—</span>
                      )}
                    </td>
                    <td>
                      {m.submitted_to_command_center ? (
                        <span
                          className="cc-badge"
                          title={`Submitted to Command Center${
                            m.command_center_submitted_by
                              ? ` by ${m.command_center_submitted_by}`
                              : ''
                          }`}
                        >
                          ⚑ CC
                        </span>
                      ) : (
                        <span style={{ color: 'var(--color-text-muted)' }}>—</span>
                      )}
                    </td>
                    <td>
                      <MentionStatusBadge
                        value={m.status}
                        editable
                        onChange={(next) => onStatusChange(m.id, next)}
                      />
                    </td>
                  </tr>
                );
              })}
            {showEmpty && (
              <tr>
                <td colSpan={COLUMNS.length}>
                  <div className="empty-state">
                    No mentions match your filters.
                    <div style={{ marginTop: 12 }}>
                      <button className="btn btn--secondary" onClick={onClearFilters}>
                        Clear filters
                      </button>
                    </div>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {!showEmpty && (
        <div className="table__pagination">
          <div>
            Showing {start}–{end} of {total} mention{total === 1 ? '' : 's'}
          </div>
          <div className="table__pagination-buttons">
            <button
              className="btn btn--secondary"
              disabled={page <= 1 || loading}
              onClick={() => onPageChange(page - 1)}
            >
              Previous
            </button>
            <button
              className="btn btn--secondary"
              disabled={page >= pages || loading || total === 0}
              onClick={() => onPageChange(page + 1)}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
